import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, Ban, FileSignature, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";

const OPTIONS = [
  ["offen", "Offen", Circle, "border-slate-300 bg-white text-slate-700", "border-slate-900 bg-slate-900 text-white"],
  ["beleg_vorhanden", "Beleg vorhanden", CheckCircle2, "border-slate-300 bg-white text-slate-700", "border-emerald-700 bg-emerald-700 text-white"],
  ["kein_beleg_noetig", "Kein Beleg nötig", Ban, "border-slate-300 bg-white text-slate-700", "border-slate-700 bg-slate-700 text-white"],
  ["eigenbeleg", "Eigenbeleg", FileSignature, "border-slate-300 bg-white text-slate-700", "border-blue-700 bg-blue-700 text-white"],
];

export const StatusButtons = ({ tx, onStatus, onEigenbeleg }) => {
  const [busy, setBusy] = useState(false);
  const set = async (s) => {
    if (s === "eigenbeleg") return onEigenbeleg();
    setBusy(true); try { await onStatus(s); } finally { setBusy(false); }
  };
  const aiNoReceipt = tx.ai?.suggested_status === "kein_beleg_noetig" && tx.status === "offen";
  return (
    <div className="grid gap-2" data-testid="status-buttons">
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map(([k, label, Icon, off, on]) => (
          <button key={k} disabled={busy} onClick={() => set(k)} data-testid={`status-btn-${k}`} data-active={tx.status === k}
            className={`flex h-12 items-center justify-center gap-2 rounded-lg border-2 text-sm font-semibold transition-colors duration-150 ${tx.status === k ? on : `${off} hover:border-slate-500`}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>
      {tx.status === "eigenbeleg" && (
        <Button variant="outline" onClick={onEigenbeleg} data-testid="edit-eigenbeleg-btn" className="gap-2"><FileSignature className="h-4 w-4" />Eigenbeleg bearbeiten / neu erzeugen</Button>
      )}
      {aiNoReceipt && (
        <Button variant="outline" onClick={() => set("kein_beleg_noetig")} disabled={busy} data-testid="accept-ai-status-btn" className="h-auto justify-start gap-2 whitespace-normal border-amber-300 bg-amber-50 py-2 text-left text-amber-900 hover:bg-amber-100">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span><b>KI-Vorschlag übernehmen:</b> Kein Beleg nötig – {tx.ai?.receipt_needed_reason}</span>
        </Button>
      )}
    </div>
  );
};

export const NotesField = ({ tx, onSaved }) => {
  const [val, setVal] = useState(tx.notes || "");
  const [state, setState] = useState("idle");
  const timer = useRef(null);
  const pending = useRef(null);
  const save = async (id, notes) => {
    setState("saving");
    try { onSaved(await api.patchTransaction(id, { notes })); setState("saved"); }
    catch (e) { setState("idle"); toast.error(errMsg(e)); }
  };
  const flush = () => { clearTimeout(timer.current); if (pending.current) { const p = pending.current; pending.current = null; save(p.id, p.notes); } };
  useEffect(() => { setVal(tx.notes || ""); setState("idle"); return flush; }, [tx.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (v) => {
    setVal(v);
    pending.current = { id: tx.id, notes: v };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 800);
  };
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor="tx-notes" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notizen</label>
        <span className="text-xs text-slate-400" data-testid="notes-save-state">
          {state === "saving" ? "Speichert …" : state === "saved" ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" />Gespeichert</span> : "Speichert automatisch"}
        </span>
      </div>
      <Textarea id="tx-notes" rows={3} value={val} onChange={(e) => change(e.target.value)} onBlur={flush} data-testid="tx-notes-input" placeholder="z. B. Beleg bei Steuerberater angefragt …" className="bg-white" />
    </div>
  );
};
